import sendAnalytics from "./analytics";

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /(?:\+?\d[\d\s\-().]{8,}\d)/g;
const CARD_PATTERN = /\b(?:\d[ -]*?){13,19}\b/g;
const TOKEN_PATTERN = /\b(?:eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)\b/g;
const LONG_NUMBER_PATTERN = /\b\d{7,}\b/g;
const SAFE_VALUE_LEN = 100;
const SAFE_KEY_LEN = 24;
const MAX_ACTION_LEN = 280;
const DEDUPE_WINDOW_MS = 8000;
const recentErrorSignatures = new Map();

const toFiniteNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const sanitizeText = (value, limit = SAFE_VALUE_LEN) => (
  String(value || "")
    .replace(EMAIL_PATTERN, "[redacted_email]")
    .replace(PHONE_PATTERN, "[redacted_phone]")
    .replace(CARD_PATTERN, "[redacted_card]")
    .replace(TOKEN_PATTERN, "[redacted_token]")
    .replace(LONG_NUMBER_PATTERN, "[redacted_number]")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, limit)
);

const sanitizeKey = (value) => (
  sanitizeText(value, SAFE_KEY_LEN)
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
);

const summarizeExtra = (extra = {}) => {
  if (!extra || typeof extra !== "object") return "";
  const segments = [];

  Object.entries(extra).forEach(([rawKey, rawValue]) => {
    if (segments.length >= 4) return;
    if (rawValue === undefined || rawValue === null) return;

    const key = sanitizeKey(rawKey);
    if (!key) return;

    if (
      typeof rawValue !== "string" &&
      typeof rawValue !== "number" &&
      typeof rawValue !== "boolean"
    ) {
      return;
    }

    const value = sanitizeText(rawValue, 45);
    if (!value) return;
    segments.push(`${key}:${value}`);
  });

  return segments.join(",");
};

const cleanupOldSignatures = () => {
  const now = Date.now();
  recentErrorSignatures.forEach((ts, key) => {
    if (now - ts > (DEDUPE_WINDOW_MS * 2)) {
      recentErrorSignatures.delete(key);
    }
  });
};

const shouldSkipDuplicate = (signature) => {
  cleanupOldSignatures();
  const now = Date.now();
  const prev = recentErrorSignatures.get(signature) || 0;
  if ((now - prev) <= DEDUPE_WINDOW_MS) return true;
  recentErrorSignatures.set(signature, now);
  return false;
};

const getFallbackRoute = () => {
  if (typeof window === "undefined") return "";
  try {
    return sanitizeText(`${window.location.pathname || ""}${window.location.hash || ""}`, 70);
  } catch {
    return "";
  }
};

export const errorToMessage = (errorLike) => {
  if (!errorLike) return "unknown_error";
  if (typeof errorLike === "string") return errorLike;
  if (typeof errorLike?.message === "string") return errorLike.message;
  try {
    return JSON.stringify(errorLike);
  } catch {
    return String(errorLike);
  }
};

export const trackFrontendError = ({
  type = "runtime",
  source = "app",
  message = "",
  route = "",
  sourcePath = "",
  endpoint = "",
  status,
  line,
  column,
  extra = {}
} = {}) => {
  const safeType = sanitizeText(type || "runtime", 40) || "runtime";
  const safeSource = sanitizeText(source || "app", 40) || "app";
  const safeMessage = sanitizeText(message || "unknown_error", SAFE_VALUE_LEN) || "unknown_error";
  const safeRoute = sanitizeText(route || getFallbackRoute(), 70);
  const safeSourcePath = sanitizeText(sourcePath, 70);
  const safeEndpoint = sanitizeText(endpoint, 70);
  const safeStatus = toFiniteNumber(status);
  const safeLine = toFiniteNumber(line);
  const safeColumn = toFiniteNumber(column);
  const safeExtra = summarizeExtra(extra);

  const signature = [
    safeType,
    safeSource,
    safeMessage,
    safeRoute,
    safeSourcePath,
    safeEndpoint,
    safeStatus ?? "",
    safeLine ?? "",
    safeColumn ?? "",
    safeExtra
  ].join("|");

  if (shouldSkipDuplicate(signature)) return false;

  const action = [
    `type:${safeType}`,
    `src:${safeSource}`,
    `msg:${safeMessage}`,
    safeRoute ? `route:${safeRoute}` : "",
    safeSourcePath ? `path:${safeSourcePath}` : "",
    safeEndpoint ? `endpoint:${safeEndpoint}` : "",
    Number.isFinite(safeStatus) ? `status:${safeStatus}` : "",
    Number.isFinite(safeLine) ? `line:${safeLine}` : "",
    Number.isFinite(safeColumn) ? `col:${safeColumn}` : "",
    safeExtra ? `meta:${safeExtra}` : ""
  ].filter(Boolean).join(" | ").slice(0, MAX_ACTION_LEN);

  try {
    sendAnalytics("FRONTEND_ERROR", action, String(new Date()));
    return true;
  } catch {
    return false;
  }
};

export default trackFrontendError;
